import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  FaReact, FaNodeJs, FaAws, FaRocket, FaShieldAlt, FaLayerGroup, 
  FaChevronDown, FaLaptopCode, FaDatabase, FaServer, FaCode, 
  FaCheckCircle, FaProjectDiagram, FaGlobe, FaTools, FaMicrochip 
} from "react-icons/fa";

import webdevImg from "../assets/images/webdev.jpg";
import "../styles/WebDevelopment.css";

const wbFaqs = [
  {
    q: "Why is Next.js the preferred choice for Enterprise SaaS?",
    a: "Next.js provides a unique combination of Server-Side Rendering (SSR) for SEO and Static Site Generation (SSG) for speed. For enterprise SaaS, this means your marketing pages rank high on Google while your authenticated dashboard remains lightning-fast and secure."
  },
  {
    q: "How do you manage cross-platform scalability?",
    a: "We utilize Microservices and API-First design. By decoupling the frontend (React/Next.js) from the backend (Node.js/Go), we ensure that your web platform can scale to millions of users across web, mobile, and IoT devices without a single point of failure."
  },
  {
    q: "What is your approach to DevOps and CI/CD?",
    a: "We implement automated pipelines using GitHub Actions or Jenkins. Every code commit undergoes rigorous automated testing (Unit, Integration, E2E) before being deployed to staging and production environments on AWS or Vercel."
  }
];

export default function WebDevelopment() {
  const [activeFaq, setActiveFaq] = useState(null);

  return (
    <div className="wb-page animated-bg">
      <Helmet>
        <title>Enterprise Full-Stack Development | Next.js & Node.js Architecture</title>
        <meta name="description" content="Global leaders in Full-Stack engineering. We build SEO-optimized Next.js platforms, high-concurrency Node.js microservices, and secure SaaS ecosystems." />
        <meta name="keywords" content="Enterprise Web Development, Next.js 15, Full Stack SaaS, React Developer, Node.js API, Microservices Architecture, Cloud Native App" />
      </Helmet>

      <div className="animated-mesh"></div>

      {/* --- HERO SECTION --- */}
      <section className="wb-hero">
        <div className="ns-container">
          <div className="wb-hero-grid">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="badge-modern wb-badge"><FaCode /> Software Engineering Excellence</div>
              <h1>Full-Stack <br/> <span className="text-gradient-wb">Digital Ecosystems</span></h1>
              <p className="ns-description">
                ITSeva Hub engineers the "Engine Room" of your digital business. We specialize in 
                <strong> Next.js 15</strong>, <strong>Distributed Microservices</strong>, and 
                <strong> Cloud-Native Infrastructure</strong> to build platforms that don't just work—they dominate.
              </p>
              <div className="ns-hero-btns">
                <Link to="/contact" className="ns-btn-primary wb-btn">Request Technical Audit</Link>
                <div className="hero-stat"><strong>Lighthouse 100</strong> <span>Performance Score</span></div>
              </div>
            </motion.div>

            <motion.div className="wb-hero-viz" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="viz-glass">
                <img src={webdevImg} alt="Enterprise Web Engineering" className="wb-main-img" />
                <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 3, repeat: Infinity }} className="floating-tag wb-t1">Next.js Architecture</motion.div>
                <motion.div animate={{ y: [0, 15, 0] }} transition={{ duration: 4, repeat: Infinity }} className="floating-tag wb-t2">API Orchestration</motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- THREE PILLARS OF DEVELOPMENT --- */}
      <section className="wb-pillars">
        <div className="ns-container">
          <div className="pillars-grid">
            <div className="pillar-item glass">
              <FaRocket />
              <h3>Velocity</h3>
              <p>Achieving sub-second load times using Incremental Static Regeneration (ISR) and Edge Computing.</p>
            </div>
            <div className="pillar-item glass">
              <FaShieldAlt />
              <h3>Security</h3>
              <p>Hardening enterprise cores with OAuth2, JWT, and SOC2-compliant encryption standards.</p>
            </div>
            <div className="pillar-item glass">
              <FaLayerGroup />
              <h3>Scalability</h3>
              <p>Architecting multi-tenant SaaS environments that scale vertically and horizontally on the cloud.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- STRATEGIC SOLUTIONS (INDUSTRIES) --- */}
      <section className="wb-industry">
        <div className="ns-container">
          <div className="section-title">
            <span className="accent-text-wb">Vertical Solutions</span>
            <h2>Tailored Digital Engineering</h2>
          </div>
          <div className="industry-grid">
            <div className="industry-card glass">
              <h4>Headless Commerce</h4>
              <p>Building high-conversion storefronts using Shopify Hydrogen or custom Next.js engines integrated with NetSuite ERP.</p>
            </div>
            <div className="industry-card glass">
              <h4>Fintech Platforms</h4>
              <p>Secure payment processing, real-time transaction monitoring, and automated financial reporting tools.</p>
            </div>
            <div className="industry-card glass">
              <h4>Enterprise SaaS</h4>
              <p>Multi-tenant architectures with complex RBAC (Role Based Access Control) and subscription lifecycle management.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- TECH STACK BENTO --- */}
      <section className="wb-services">
        <div className="ns-container">
          <div className="section-title center">
            <span className="accent-text-wb">The Tech Stack</span>
            <h2>Modern Engineering Layers</h2>
          </div>

          <div className="bento-grid">
            <motion.div whileHover={{ y: -5 }} className="bento-card glass tall">
              <FaReact className="b-icon wb-indigo" />
              <h3>Frontend: The UX Core</h3>
              <p>Utilizing <strong>React 19 Server Components</strong> for a seamless, state-driven user experience.</p>
              <ul className="bento-list">
                <li><FaCheckCircle /> Tailwind CSS & Framer Motion</li>
                <li><FaCheckCircle /> Zustand & TanStack Query</li>
                <li><FaCheckCircle /> PWA (Progressive Web Apps)</li>
              </ul>
            </motion.div>

            <div className="bento-right-stack">
              <motion.div whileHover={{ y: -5 }} className="bento-card glass wide">
                <FaNodeJs className="b-icon wb-green" />
                <div>
                  <h3>Backend: High-Concurrency Engines</h3>
                  <p>Developing <strong>Node.js and NestJS</strong> backends that handle data-intensive operations with zero downtime.</p>
                </div>
              </motion.div>

              <div className="bento-inner-grid">
                <motion.div whileHover={{ y: -5 }} className="bento-card glass">
                  <FaDatabase className="b-icon wb-pink" />
                  <h3>Data Tier</h3>
                  <p>SQL & NoSQL optimization with Prisma ORM.</p>
                </motion.div>
                <motion.div whileHover={{ y: -5 }} className="bento-card glass">
                  <FaAws className="b-icon wb-orange" />
                  <h3>Infrastructure</h3>
                  <p>Docker, Kubernetes, and Serverless deployments.</p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- DEVELOPMENT LIFECYCLE (THE "HOW") --- */}
      <section className="wb-lifecycle">
        <div className="ns-container">
          <div className="section-title center">
            <span className="accent-text-wb">The Process</span>
            <h2>Our Development Lifecycle</h2>
          </div>
          <div className="lifecycle-grid">
            {[
              { icon: <FaGlobe />, title: "Discovery", desc: "Technical audit, stack selection, and scalability roadmap." },
              { icon: <FaProjectDiagram />, title: "Architecture", desc: "Schema design, API documentation, and UI prototyping." },
              { icon: <FaMicrochip />, title: "Engineering", desc: "Agile sprints, daily stand-ups, and 100% code coverage." },
              { icon: <FaTools />, title: "Governance", desc: "CI/CD deployment, security hardening, and 24/7 monitoring." }
            ].map((step, idx) => (
              <div className="step-card" key={idx}>
                <div className="step-num">{idx + 1}</div>
                {step.icon}
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="wb-faq">
        <div className="ns-container narrow">
          <h2 className="center">Engineering Insights & FAQ</h2>
          <div className="faq-list">
            {wbFaqs.map((faq, index) => (
              <div className={`faq-item glass ${activeFaq === index ? "active" : ""}`} key={index}>
                <button onClick={() => setActiveFaq(activeFaq === index ? null : index)}>
                  {faq.q} <FaChevronDown className={activeFaq === index ? "rotate" : ""} />
                </button>
                <AnimatePresence>
                  {activeFaq === index && (
                    <motion.div className="faq-answer" initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}>
                      <p>{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="wb-cta">
        <div className="ns-container center">
          <div className="cta-glass">
            <h2>Let's Build Something Exceptional</h2>
            <p>Consult with our senior architects for your next enterprise project.</p>
            <Link to="/contact" className="btn-main-glow wb-glow">Talk to an Architect</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}